import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, X, Cable, ShoppingBag, Footprints, Wrench, Layers, SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import { API, useLang, useSettings } from "../lib/providers";
import ProductCard from "./ProductCard";
import { RacketIcon, BallIcon } from "./icons/TennisIcons";

// Map each category id → icon component (custom SVG for racket/ball, lucide for rest)
const CATEGORY_ICONS = {
  all: Layers,
  racket: RacketIcon,
  string: Cable,
  bag: ShoppingBag,
  shoe: Footprints,
  accessory: Wrench,
  balls: BallIcon,
};

// 6 filterable spec fields with labels (FR / AR)
const SPEC_FILTERS = [
  { key: "spec_weight", fr: "Poids", ar: "الوزن" },
  { key: "spec_balance", fr: "Équilibre", ar: "التوازن" },
  { key: "spec_head_size", fr: "Taille tamis", ar: "حجم الرأس" },
  { key: "spec_string_pattern", fr: "Plan cordage", ar: "نمط الأوتار" },
  { key: "spec_format", fr: "Format", ar: "التنسيق" },
  { key: "spec_size", fr: "Taille / Pointure", ar: "المقاس" },
];

export default function ProductGrid() {
  const { lang } = useLang();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [reviewStats, setReviewStats] = useState({}); // {product_id: {count, avg}}
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [specFilters, setSpecFilters] = useState({});  // { spec_weight: Set("300g","285g"), ... }
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("relevance"); // relevance | price_asc | price_desc | newest
  const gridRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch((e) => console.error(e)).finally(() => setLoading(false));
    // Reviews aggregate per product (used for star widget on each card)
    axios.get(`${API}/reviews/public/products-stats`).then((r) => setReviewStats(r.data || {})).catch(() => {});
  }, []);

  // Listen to global search events from Header
  useEffect(() => {
    const onSearch = (ev) => setSearch(ev.detail?.q || "");
    window.addEventListener("global-search", onSearch);
    return () => window.removeEventListener("global-search", onSearch);
  }, []);

  const catLabels = lang === "ar" ? settings.product_category_labels_ar : settings.product_category_labels_fr;
  const label = (c) => (c === "all" ? (lang === "ar" ? "الكل" : "Tout") : (catLabels?.[c] || c));

  // Menu dynamique (Partie 2) : catégories distinctes des produits ACTIFS,
  // ordonnées d'abord selon settings.product_categories (ordre admin) puis
  // les nouveautés apparues en base — pas de liste codée en dur.
  const distinctCategories = useMemo(() => {
    const set = new Set();
    for (const p of products) {
      if (p.active !== false && p.category) set.add(p.category);
    }
    const settingsOrder = settings.product_categories || [];
    const ordered = settingsOrder.filter((c) => set.has(c));
    const extras = [...set].filter((c) => !settingsOrder.includes(c)).sort();
    return [...ordered, ...extras];
  }, [products, settings.product_categories]);

  const categories = ["all", ...distinctCategories];

  // Slug rule (aligné avec CategoryPage) : minuscule + underscores → tirets
  const toSlug = (c) => String(c).toLowerCase().replace(/_/g, "-");

  // Count per category (across all visible products, ignoring search so the user sees total counts)
  const catCounts = useMemo(() => {
    const m = { all: products.length };
    for (const p of products) m[p.category] = (m[p.category] || 0) + 1;
    return m;
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = products.filter((p) => {
      if (cat !== "all" && p.category !== cat) return false;
      // Spec filters: each filter is a Set of selected values; product must match at least one in each active set
      for (const [k, valuesSet] of Object.entries(specFilters)) {
        if (valuesSet && valuesSet.size > 0) {
          const v = k.startsWith("custom:")
            ? ((p.custom_specs || {})[k.slice(7)] || "").trim()
            : (p[k] || "").trim();
          if (!v || !valuesSet.has(v)) return false;
        }
      }
      if (!q) return true;
      const hay = [p.name, p.brand, p.description, ...(p.tags_fr || [])].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
    // Sort
    const priceOf = (p) => {
      const rate = settings.exchange_rate || 280;
      return p.price_dzd > 0 ? p.price_dzd : (p.price_eur || 0) * rate;
    };
    const sorted = [...list];
    if (sortBy === "price_asc") sorted.sort((a, b) => priceOf(a) - priceOf(b));
    else if (sortBy === "price_desc") sorted.sort((a, b) => priceOf(b) - priceOf(a));
    else if (sortBy === "newest") sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    // Ruptures de stock en dernier (tri stable : l'ordre choisi est conservé dans chaque groupe)
    const isSold = (p) => (p.stock_status === "out_of_stock" ? 1 : 0);
    return sorted.sort((a, b) => isSold(a) - isSold(b));
  }, [products, cat, search, specFilters, sortBy, settings.exchange_rate]);

  // Combined filter list: 6 built-in + admin-defined custom filters
  const allFilters = useMemo(() => {
    const builtIn = SPEC_FILTERS;
    const custom = (settings.custom_filters || [])
      .filter((f) => f.id && f.label_fr)
      .map((f) => ({ key: `custom:${f.id}`, fr: f.label_fr, ar: f.label_ar || f.label_fr, custom: true, _raw: f.id }));
    return [...builtIn, ...custom];
  }, [settings.custom_filters]);

  // Distinct values per spec, computed only from products of the current category
  const availableSpecs = useMemo(() => {
    const scope = products.filter((p) => cat === "all" || p.category === cat);
    const map = {};
    for (const f of allFilters) {
      const set = new Set();
      for (const p of scope) {
        const v = f.key.startsWith("custom:")
          ? ((p.custom_specs || {})[f.key.slice(7)] || "").trim()
          : (p[f.key] || "").trim();
        if (v) set.add(v);
      }
      map[f.key] = Array.from(set).sort();
    }
    return map;
  }, [products, cat, allFilters]);

  const totalActiveFilters = Object.values(specFilters).reduce((acc, s) => acc + (s?.size || 0), 0);

  const toggleSpecValue = (key, val) => {
    setSpecFilters((prev) => {
      const next = { ...prev };
      const current = new Set(next[key] || []);
      if (current.has(val)) current.delete(val); else current.add(val);
      if (current.size === 0) delete next[key]; else next[key] = current;
      return next;
    });
  };

  const clearAllFilters = () => setSpecFilters({});

  return (
    <section id="products" className="py-20 sm:py-28 relative theme-section-alt" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 mb-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-neon mb-3">
                {lang === "ar" ? "مضارب، أوتار، حقائب وأحذية" : "Raquettes, cordages, sacs & chaussures"}
              </p>
              <h2 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl">
                {lang === "ar" ? "المنتجات" : "Catalogue"}
              </h2>
            </div>
            {/* Search bar */}
            <div className="relative w-full sm:max-w-sm">
              <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={lang === "ar" ? "ابحث عن منتج..." : "Rechercher un produit…"}
                className="w-full bg-black/40 border border-white/10 text-white ps-9 pe-9 py-2.5 text-sm outline-none focus:border-neon transition-colors"
                data-testid="product-search-input"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute end-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                  data-testid="product-search-clear"
                  aria-label="clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Category tiles — visual gateway: clic = filter + smooth scroll to grid */}
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-4 lg:grid-cols-7 sm:gap-3 sm:overflow-visible sm:pb-0" data-testid="category-tiles">
            {categories.map((c) => {
              const n = catCounts[c] || 0;
              if (c !== "all" && n === 0) return null;
              const Icon = CATEGORY_ICONS[c] || Layers;
              const active = cat === c;
              return (
                <button
                  key={c}
                  onClick={() => {
                    if (c === "all") {
                      // In-page reset for the home catalog
                      setCat("all");
                      setSpecFilters({});
                      setTimeout(() => gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                    } else {
                      // Dedicated category page — shareable URL
                      navigate(`/categorie/${toSlug(c)}`);
                    }
                  }}
                  className={`group relative flex flex-row sm:flex-col items-center justify-center gap-2 shrink-0 min-h-[44px] sm:min-h-0 rounded-full sm:rounded-none px-4 sm:p-3 sm:aspect-[4/3] border transition-all duration-200 overflow-hidden ${
                    active
                      ? "bg-neon text-black border-neon shadow-[0_0_30px_rgba(204,255,0,0.4)] scale-[1.02]"
                      : "bg-black/40 border-white/10 text-white/80 hover:border-neon/50 hover:bg-black/60 hover:text-white hover:scale-[1.02]"
                  }`}
                  data-testid={`cat-tile-${c}`}
                >
                  <Icon className={`hidden sm:block w-7 h-7 sm:w-9 sm:h-9 transition-transform group-hover:scale-110 ${active ? "" : "text-neon"}`} strokeWidth={1.6} />
                  <span className="text-xs font-black uppercase tracking-widest text-center leading-tight whitespace-nowrap sm:whitespace-normal">
                    {label(c)}
                  </span>
                  <span className={`text-[11px] font-bold ${active ? "text-black/60" : "text-white/40"}`} data-testid={`cat-count-${c}`}>
                    {n}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Filters bar — collapsible, only shows specs that have values for current scope */}
          {(() => {
            const hasAnySpec = allFilters.some((f) => (availableSpecs[f.key] || []).length > 0);
            if (!hasAnySpec) return null;
            return (
              <div className="border border-white/10 bg-black/30" data-testid="spec-filters-bar">
                <button
                  onClick={() => setShowFilters((s) => !s)}
                  className="w-full flex items-center justify-between px-4 py-3 text-xs font-black uppercase tracking-widest text-white/80 hover:bg-black/40 transition-colors"
                  data-testid="toggle-spec-filters"
                >
                  <span className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-neon" />
                    {lang === "ar" ? "الفلاتر" : "Filtres"}
                    {totalActiveFilters > 0 && (
                      <span className="bg-neon text-black px-1.5 py-0.5 text-[10px]" data-testid="active-filters-count">
                        {totalActiveFilters}
                      </span>
                    )}
                  </span>
                  {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showFilters && (
                  <div className="border-t border-white/10 p-4 space-y-3">
                    {allFilters.map((f) => {
                      const values = availableSpecs[f.key] || [];
                      if (values.length === 0) return null;
                      const selected = specFilters[f.key] || new Set();
                      return (
                        <div key={f.key} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                          <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold sm:w-32 sm:pt-1.5 sm:shrink-0">
                            {f.custom && "✨ "}{lang === "ar" ? f.ar : f.fr}
                          </div>
                          <div className="flex flex-wrap gap-2 flex-1">
                            {values.map((v) => {
                              const isOn = selected.has(v);
                              return (
                                <button
                                  key={v}
                                  onClick={() => toggleSpecValue(f.key, v)}
                                  className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border transition-colors ${
                                    isOn
                                      ? "bg-neon text-black border-neon"
                                      : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                                  }`}
                                  data-testid={`filter-${f.key.replace(":", "_")}-${v.replace(/\s+/g, "_")}`}
                                >
                                  {v}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {totalActiveFilters > 0 && (
                      <button
                        onClick={clearAllFilters}
                        className="text-[11px] text-clay hover:text-white underline underline-offset-2"
                        data-testid="clear-all-filters"
                      >
                        ✕ {lang === "ar" ? "مسح كل الفلاتر" : "Tout effacer"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {/* Sort dropdown + count */}
          <div className="flex items-center justify-between gap-3 text-xs">
            <div className="text-white/50" data-testid="products-count">
              {lang === "ar" ? `${filtered.length} منتج` : `${filtered.length} produit${filtered.length > 1 ? "s" : ""}`}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-widest text-white/50 hidden sm:inline">
                {lang === "ar" ? "الترتيب" : "Trier"} :
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-black/40 border border-white/10 text-white text-xs px-3 py-1.5 outline-none focus:border-neon transition-colors font-bold appearance-none cursor-pointer"
                data-testid="product-sort-select"
              >
                <option value="relevance">{lang === "ar" ? "الأنسب" : "Pertinence"}</option>
                <option value="price_asc">{lang === "ar" ? "السعر ↑" : "Prix croissant ↑"}</option>
                <option value="price_desc">{lang === "ar" ? "السعر ↓" : "Prix décroissant ↓"}</option>
                <option value="newest">{lang === "ar" ? "الأحدث" : "Nouveautés"}</option>
              </select>
            </div>
          </div>

        </div>

        <div ref={gridRef}>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="aspect-[3/4] bg-black/40 animate-pulse" />))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-white/40 border border-dashed border-white/10" data-testid="products-empty">
              {search
                ? (lang === "ar" ? `لا نتائج لـ "${search}"` : `Aucun résultat pour « ${search} ».`)
                : (lang === "ar" ? "لا يوجد منتج." : "Aucun produit.")}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="products-grid">
              {filtered.map((p, i) => (<ProductCard key={p.id} product={p} priority={i < 4} reviewStats={reviewStats[p.id]} onClick={() => navigate(`/produit/${p.id}`)} />))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
